<template>
  <section
    class="booking"
    @mouseleave="removePopIn"
    v-click-outside="removeOnUnFocused"
  >
    <custom-input
      class="-borderless"
      ref="customInput"
      :value="value"
      :placeholder="placeholders"
      :active="active"
      :alert="alert"
      :loading="loading"

      @input="updateBooking"
      @focus="handleFocus"
      @blur="handleBlur"
      @hover="handleHover"
      @key="handleUserInput"
    >
      <template
        v-if="geolocation"
        v-slot:icon
      >
        <div class="target-svg-wrapper">
          <basic-prompt
            hoverable
            :position="{top: 'auto', bottom: 0, right: 0, left: 'auto'}"
          >
            <template v-slot:visible>
              <svg
                @click="runGeolocation"
                class="target-svg"
              >
                <use xlink:href="#icon-target"></use>
              </svg>
            </template>
            <template v-slot:prompt>
              <div
                class="tooltip"
                v-html="$t('components.booking.geolocation_tooltip')"
              />
            </template>
          </basic-prompt>
        </div>
      </template>
    </custom-input>
    <booking-input-list
      v-if="filteredSearch"
      :list="filteredSearch"
      :return-list="drop"
      @selected="selectedFromList"
      @hoveredBusiness="hoveredBusiness"
      @focusinput="focusInput"
      :showing-business="lastBusinessResults"
      :trigger-focus-list="triggerFocusList"
    />
    <booking-pop-in
      v-if="!drop && popinValues"
      :business="popinValues"
      @agencyselected="selectedFromList"
      :show-details-left="showDetailsLeft"
    />
    <section
      class="booking__oneway-declined"
      v-if="oneWay && active && !isOneWayAgency"
    >
      <div class="booking__oneway-declined__message">
        <p v-html="$t('components.booking.noWay', {slug: pickUpBusiness.slug})" />
      </div>
    </section>
  </section>
</template>
<script>
import { mapState } from 'vuex'
import axios from 'axios'
import ClickOutside from 'vue-click-outside'
import debounce from 'lodash-es/debounce'
import CustomInput from '../shared/inputs/CustomInput'
import BookingPopIn from './BookingPopIn'
import BasicPrompt from '../shared/BasicPrompt'

export default {
  name: 'BookingInput',
  props: {
    value: {
      type: String,
      default: '',
    },
    active: {
      required: true,
      default: true,
      validator: (prop) => typeof prop === 'boolean' || prop === null,
    },
    alert: {
      type: Boolean,
      required: true,
      default: false,
    },
    drop: {
      type: Boolean,
      required: true,
      default: false,
    },
    showDetailsLeft: {
      type: Boolean,
      default: false,
    },
    geolocation: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    CustomInput,
    BookingInputList: () => import(/* webpackChunkName: "booking"  */ `./BookingInputList`),
    BookingPopIn,
    BasicPrompt,
  },
  data() {
    return {
      placeholders: this.$t('components.booking.geolocation'),
      inputValue: null,
      list: [],
      filteredSearch: [],
      lastBusinessResults: null,
      cities: true,
      popinValues: null,
      loading: false,
      isOneWayAgency: true,
      isOneWayFetchable: false,
      triggerFocusList: false,
    }
  },
  computed: {
    ...mapState({
      pickUpBusiness: (state) => state.booking.pickUp.business,
      businessLocalStorageChecked: (state) => state.booking.businessLocalStorageChecked,
      oneWay: (state) => state.booking.oneWay,
      api: (state) => state.api,
    }),
  },
  created() {
    this.updateBooking = debounce(this.debouncedUpdateBooking, 500)
    this.GEOLOCATION = navigator.geolocation
  },
  mounted() {
    this.handleOneWayListDisplay()
    if (this.drop) {
      this.cities = false
      this.getOneWay()
      return
    }
    this.cities = true
  },
  watch: {
    inputValue(val) {
      // fetch the OneWay list so there is no list will be automaticly filled
      if (this.drop) return
      // update list of cities when use typed in the input
      return !this.value && val.length ? this.getCities(val) : ''
    },
    pickUpBusiness() {
      this.handleOneWayListDisplay()
      return this.drop ? this.getOneWay() : ''
    },
    oneWay(newVal, oldVal) {
      this.handleOneWayListDisplay()
      if (!this.drop) {
        return
      }
      if (newVal) {
        return this.getOneWay()
      }
      if (oldVal) {
        this.$emit('clearvalue')
      }
    },
    value() {
      if (!this.drop || !this.value) {
        return
      }
      if (!this.oneWay) {
        this.$emit('clearvalue')
      }
    },
    list() {
      this.filteredSearch = this.list
    },
    geolocation: {
      handler() {
        if (!localStorage.getItem('rentPickupBusinessId')) this.runGeolocation()
      },
      deep: true,
    },
  },
  directives: {
    ClickOutside,
  },
  methods: {
    handleOneWayListDisplay() {
      if (!this.businessLocalStorageChecked || !this.drop || !this.pickUpBusiness || !this.oneWay) {
        this.isOneWayFetchable = false
      } else {
        this.isOneWayFetchable = true
      }
    },
    debouncedUpdateBooking(value) {
      this.inputValue = value
      this.filterFetchedList()
    },
    getCities(value) {
      // change the list layout to the cities layout
      this.cities = true
      this.loading = true
      this.lastBusinessResults = null
      this.list = null
      // fetch cities
      axios
        .get(`/api/cities/search?search=${value}`)
        .then((response) => {
          this.list = response.data.data
          this.loading = false
        })
        .catch((error) => {
          console.log(error)
        })
    },
    filterFetchedList() {
      if (!this.drop || !this.inputValue) return
      // user can fetch one way agency in list
      this.filteredSearch = this.list.filter((results) => results.label
        .toLowerCase()
        .includes(this.inputValue.toLowerCase()))
    },
    async getBusiness(value) {
      this.cities = false
      this.loading = true
      this.$emit('focus', value.label)
      await axios
        .post(`${this.api}/businesses/search`, {
          city_proxy: value.id,
          zipcode: value.postal_code,
          latitude: value.latitude,
          longitude: value.longitude,
          only_oneway: this.oneWay,
        })
        .then((response) => {
          this.list = response.data.data
          this.lastBusinessResults = this.list
          this.loading = false
        })
        .catch((error) => {
          console.log(error)
        })
    },
    async getOneWay() {
      if (!this.isOneWayFetchable) return
      this.loading = true

      try {
        if (this.pickUpBusiness) {
          const response = await axios.get(
            `${this.api}/businesses/${this.pickUpBusiness.id}/oneway`,
          )
          this.list = response.data.data
          if (!this.list.length) {
            this.isOneWayAgency = false
            return
          }
          this.isOneWayAgency = true
          return
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    handleFocus() {
      // clear value on the current input when user click on the input
      this.$emit('focus')
      this.handleOneWayListDisplay()
      this.getOneWay()
      if (this.lastBusinessResults && this.businessLocalStorageChecked) {
        this.list = this.lastBusinessResults
      }
    },
    handleUserInput(ev) {
      const focusList = ev.key === 'ArrowDown' || ev.key === 'Tab'
      if (!focusList) {
        return
      }
      this.triggerFocusList = !this.triggerFocusList
    },
    handleHover() {},
    handleBlur() {
      this.$emit('blur')
    },
    hoveredBusiness(val) {
      if (this.cities) return
      this.popinValues = val
    },
    removePopIn() {
      this.popinValues = null
    },
    removeOnUnFocused() {
      this.popinValues = null
      this.list = []
    },
    runGeolocation() {
      this.GEOLOCATION.getCurrentPosition((pos) => {
        this.getBusiness({ latitude: pos.coords.latitude, longitude: pos.coords.longitude })
      })
    },
    selectedFromList(val) {
      this.popinValues = null

      this.list = []
      if (!this.cities) {
        return this.$emit('selected', val)
      }
      return this.getBusiness(val)
    },
    focusInput() {
      this.$refs.customInput.$refs.input.focus()
    },

  },
}
</script>

<style scoped lang="scss">

.booking {
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;

  &__oneway-declined {
    margin-bottom: 5rem;
    position: relative;

    @include teq-breakpoint($large) {
      margin-bottom: 0;
    }

    &__message {
      background-color: $sunglow;
      color: $black;
      padding: 10px;
      position: absolute;
      transform: translate(0%, 10px);

      p {
        @include font-size(11px);

        margin-bottom: 0;
      }

      & ::v-deep a {
        color: $black;
        text-decoration: underline;
      }
    }
  }
}

.target-svg-wrapper {
  align-items: center;
  bottom: 0;
  display: flex;
  justify-content: center;
  position: absolute;
  right: 0;
  top: 0;
  width: 36px;

  &::after {
    background: #adafaf;
    content: "";
    height: 80%;
    left: 0;
    position: absolute;
    width: 1px;
  }
}

.target-svg-wrapper ::v-deep .clickable-txt {
  align-items: center;
  display: flex;
}

.target-svg {
  cursor: pointer;
  fill: #878785;
  height: 17px;
  position: relative;
  width: 17.66px;
  z-index: 3;
}

.tooltip {
  color: black;
}
</style>
